import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Boxes,
  CalendarDays,
  CircleAlert,
  ClipboardList,
  Rocket,
  FolderKanban,
  Home,
  KeyRound,
  MessageCircle,
  Plus,
  Settings,
  TriangleAlert,
  User,
  Users,
  Webhook,
} from 'lucide-react';
import { projectWorkPath, useProjectIdFromPath } from '../../lib/project-path';
import { isWorkspaceProject } from '../../lib/workspace-projects';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { apiFetch, v1Path } from '../../lib/api';
import type { ProjectDto, WorkItemSlim } from '../../lib/work-types';
import { listWikiPages, parseWikiLocation, wikiRoute, type WikiPageSlim, type WikiSpaceRef } from '../../lib/wiki';
import { useSession } from '../session-context';
import { useOverlays } from './overlay-context';

type WikiHit = { space: WikiSpaceRef; page: WikiPageSlim };

const WIKI_HITS_PER_SPACE = 6;
const ORG_SPACE: WikiSpaceRef = { kind: 'org', projectId: null };

export default function PowerK() {
  const { powerKOpen, setPowerKOpen, openCreateWorkItem, openCreateProject, openCreateView } =
    useOverlays();
  const { isOrgAdmin } = useSession();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const projectId = useProjectIdFromPath();
  const wikiLocation = parseWikiLocation(pathname);
  // The project whose wiki is searched alongside the org wiki: the open wiki space, else the open project.
  const contextProjectId = wikiLocation?.space.projectId ?? projectId ?? null;
  const [query, setQuery] = useState('');
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [items, setItems] = useState<WorkItemSlim[]>([]);
  const [wikiHits, setWikiHits] = useState<WikiHit[]>([]);

  useEffect(() => {
    if (!powerKOpen) return;
    setQuery('');
    void apiFetch<{ items: ProjectDto[] }>(v1Path('/projects?limit=50&offset=0'))
      .then((res) => setProjects(res.items))
      .catch(() => setProjects([]));
  }, [powerKOpen]);

  useEffect(() => {
    if (!powerKOpen || !projectId || query.trim().length < 1) {
      setItems([]);
      return;
    }
    const handle = window.setTimeout(() => {
      void apiFetch<{ items: WorkItemSlim[] }>(
        `${v1Path(`/projects/${projectId}/work-items`)}?query=${encodeURIComponent(query.trim())}&limit=8`,
      )
        .then((res) => setItems(res.items))
        .catch(() => setItems([]));
    }, 200);
    return () => window.clearTimeout(handle);
  }, [powerKOpen, projectId, query]);

  useEffect(() => {
    const needle = query.trim();
    if (!powerKOpen || needle.length < 1) {
      setWikiHits([]);
      return;
    }
    const spaces: WikiSpaceRef[] = contextProjectId
      ? [ORG_SPACE, { kind: 'project', projectId: contextProjectId }]
      : [ORG_SPACE];
    let cancelled = false;
    const handle = window.setTimeout(() => {
      void Promise.all(
        spaces.map((space) =>
          listWikiPages(space, needle)
            .then((rows) => rows.slice(0, WIKI_HITS_PER_SPACE).map((page): WikiHit => ({ space, page })))
            // A space the user cannot read (403) or that failed to load simply contributes no hits.
            .catch((): WikiHit[] => []),
        ),
      ).then((groups) => {
        if (!cancelled) setWikiHits(groups.flat());
      });
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [powerKOpen, contextProjectId, query]);

  function go(path: string) {
    setPowerKOpen(false);
    navigate(path);
  }

  function createWikiPage() {
    setPowerKOpen(false);
    if (wikiLocation) {
      // The space page owns the dialog; `?create=1` opens it under the page being viewed.
      const params = new URLSearchParams(search);
      params.set('create', '1');
      navigate({ pathname, search: `?${params.toString()}` });
      return;
    }
    const space: WikiSpaceRef = contextProjectId ? { kind: 'project', projectId: contextProjectId } : ORG_SPACE;
    navigate(`${wikiRoute(space)}?create=1`);
  }

  return (
    <CommandDialog open={powerKOpen} onOpenChange={setPowerKOpen}>
      <CommandInput
        placeholder="Search or jump…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>No results.</CommandEmpty>
        <CommandGroup heading="Create">
          <CommandItem
            onSelect={() => go(projectId ? `/service-requests/new?project=${projectId}` : '/service-requests/new')}
          >
            <ClipboardList />
            Create Service Request
          </CommandItem>
          <CommandItem
            onSelect={() => {
              setPowerKOpen(false);
              openCreateWorkItem({ projectId });
            }}
          >
            <Plus />
            Create work item
          </CommandItem>
          <CommandItem
            onSelect={() => {
              setPowerKOpen(false);
              openCreateWorkItem({ type: 'incident', projectId });
            }}
          >
            <TriangleAlert />
            Create incident
          </CommandItem>
          <CommandItem
            onSelect={() => {
              setPowerKOpen(false);
              openCreateWorkItem({ type: 'problem', projectId });
            }}
          >
            <CircleAlert />
            Create problem
          </CommandItem>
          <CommandItem
            onSelect={() => {
              setPowerKOpen(false);
              openCreateProject();
            }}
          >
            <Plus />
            Create stream
          </CommandItem>
          <CommandItem
            onSelect={() => {
              setPowerKOpen(false);
              openCreateView({ projectId });
            }}
          >
            <Plus />
            Create view
          </CommandItem>
          <CommandItem onSelect={createWikiPage}>
            <Plus />
            Create wiki page
          </CommandItem>
        </CommandGroup>
        {wikiHits.length > 0 ? (
          <CommandGroup heading="Wiki pages">
            {wikiHits.map(({ space, page }) => (
              <CommandItem
                key={page.id}
                // cmdk reads an item's value from its DOM text, but an item that mounts while a
                // search is active is filtered out before it reaches the DOM, so it never gets a
                // value and never matches. An explicit value plus the query as a keyword keeps
                // every server hit (title or body match) visible.
                value={page.id}
                keywords={[query.trim()]}
                onSelect={() => go(wikiRoute(space, page.path))}
              >
                <BookOpen />
                <span className="truncate">{page.title}</span>
                <span className="ml-auto truncate text-xs text-muted-foreground">
                  {space.kind === 'org' ? 'Org' : 'Stream'} · {page.path}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
        {items.length > 0 ? (
          <CommandGroup heading="Work items">
            {items.map((item) => (
              <CommandItem
                key={item.id}
                // Same async-mount workaround as the wiki hits below.
                value={item.id}
                keywords={[query.trim()]}
                onSelect={() => go(`/projects/${item.project_id}/work-items/${item.id}`)}
              >
                <span className="font-mono text-xs mr-2">{item.key}</span>
                {item.title}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
        <CommandGroup heading="Streams">
          {projects
            .filter((p) => !isWorkspaceProject(p))
            .filter((p) => !query || p.name.toLowerCase().includes(query.toLowerCase())
              || p.identifier.toLowerCase().includes(query.toLowerCase()))
            .map((project) => (
              <CommandItem
                key={project.id}
                onSelect={() => go(projectWorkPath(project.id))}
              >
                <FolderKanban />
                {project.identifier} · {project.name}
              </CommandItem>
            ))}
        </CommandGroup>
        <CommandGroup heading="Go to">
          <CommandItem onSelect={() => go('/')}>
            <Home />
            My work
          </CommandItem>
          <CommandItem onSelect={() => go('/incidents')}>
            <TriangleAlert />
            Incidents
          </CommandItem>
          <CommandItem onSelect={() => go('/problems')}>
            <CircleAlert />
            Problems
          </CommandItem>
          <CommandItem onSelect={() => go('/service-requests')}>
            <ClipboardList />
            Service Requests
          </CommandItem>
          <CommandItem onSelect={() => go('/service-requests/calendar')}>
            <CalendarDays />
            Service Request calendar
          </CommandItem>
          <CommandItem onSelect={() => go('/service-requests/implementations')}>
            <Boxes />
            Implementations
          </CommandItem>
          <CommandItem onSelect={() => go('/projects')}>
            <FolderKanban />
            Streams
          </CommandItem>
          <CommandItem onSelect={() => go('/releases')}>
            <Rocket />
            Releases
          </CommandItem>
          <CommandItem onSelect={() => go('/wiki')}>
            <BookOpen />
            Wiki
          </CommandItem>
          <CommandItem onSelect={() => go('/settings/profile')}>
            <User />
            Settings · Profile
          </CommandItem>
          <CommandItem onSelect={() => go('/settings/members')}>
            <Users />
            Settings · Members
          </CommandItem>
          <CommandItem onSelect={() => go('/settings/tokens')}>
            <KeyRound />
            Settings · Tokens
          </CommandItem>
          {isOrgAdmin ? (
            <CommandItem onSelect={() => go('/settings/service-requests')}>
              <ClipboardList />
              Settings · Service Requests
            </CommandItem>
          ) : null}
          {isOrgAdmin ? (
            <CommandItem onSelect={() => go('/settings/webhooks')}>
              <Webhook />
              Settings · Webhooks
            </CommandItem>
          ) : null}
          {isOrgAdmin ? (
            <CommandItem onSelect={() => go('/settings/whatsapp')}>
              <MessageCircle />
              Settings · WhatsApp
            </CommandItem>
          ) : null}
          {projectId ? (
            <CommandItem onSelect={() => go(`/projects/${projectId}/settings`)}>
              <Settings />
              Stream settings
            </CommandItem>
          ) : null}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
